import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useApp } from "../lib/store";
import { FIELDS } from "../lib/fields";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { SearchSelect, SearchMultiSelect } from "./SearchSelect";
import { toast } from "sonner";
import { errDetail } from "../lib/api";
import { Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

export function EntityForm({ module, initial = {}, onSaved, onCancel, createUrl, itemUrl, confirmSave = false, allowDelete = false, onDeleted }) {
  const { t, plOptions, reloadPicklists } = useApp();
  const [form, setForm] = useState({ ...initial });
  const [locations, setLocations] = useState([]);
  const [plants, setPlants] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dupWarning, setDupWarning] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const fields = FIELDS[module];
  const _createUrl = createUrl || `/${module}`;
  const _itemUrl = (id) => (itemUrl ? itemUrl(id) : `/${module}/${id}`);

  useEffect(() => {
    reloadPicklists();
    if (fields.some((f) => f.type === "location_ref")) api.get("/locations?limit=1000").then((r) => setLocations(r.data)).catch(() => {});
    if (fields.some((f) => ["plant_multi", "plant_ref"].includes(f.type)))
      api.get("/iam/plants?page_size=200").then((r) => setPlants(r.data.items || [])).catch(() => {});
    if (fields.some((f) => f.type === "user_multi"))
      api.get("/iam/users?page_size=200").then((r) => setUsers(r.data.items || [])).catch(() => {});
  }, [fields, reloadPicklists]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const checkDuplicate = async (name) => {
    if (!name || initial.id || !["accounts", "contacts"].includes(module)) return;
    try {
      const r = await api.get(`/${module}?q=${encodeURIComponent(name)}`);
      const hit = r.data.find((d) => (d.name || `${d.first_name} ${d.last_name}`).toLowerCase().includes(name.toLowerCase()));
      setDupWarning(hit ? `${t("possible_duplicate")}: ${hit.name || hit.first_name + " " + hit.last_name}` : "");
    } catch {}
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (body.location_id) {
        const l = locations.find((x) => x.id === body.location_id);
        if (l) body.location_name = l.name;
      }
      const res = initial.id
        ? await api.put(_itemUrl(initial.id), body)
        : await api.post(_createUrl, body);
      toast.success(t("save") + " ✓");
      onSaved?.(res.data);
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = () => {
    if (confirmSave && initial.id) setConfirmOpen(true);
    else save();
  };

  const doDelete = async () => {
    setSaving(true);
    try {
      await api.delete(_itemUrl(initial.id));
      toast.success(t("contact_deleted") + " ✓");
      setDeleteOpen(false);
      onDeleted?.();
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f) => {
    const val = form[f.key];
    if (f.type === "text" || f.type === "number" || f.type === "date") {
      return (
        <Input data-testid={`form-${module}-${f.key}`} type={f.type === "text" ? "text" : f.type}
          value={val ?? ""} onChange={(e) => set(f.key, f.type === "number" ? Number(e.target.value) : e.target.value)}
          onBlur={f.key === "name" || f.key === "last_name" ? (e) => checkDuplicate(e.target.value) : undefined} />
      );
    }
    if (f.type === "textarea") return <Textarea data-testid={`form-${module}-${f.key}`} value={val ?? ""} onChange={(e) => set(f.key, e.target.value)} />;
    if (f.type === "select") {
      const opts = f.optionsList ? f.optionsList.map((s) => ({ id: s, label: s })) : plOptions(f.options);
      return (
        <Select value={val || ""} onValueChange={(v) => set(f.key, v)}>
          <SelectTrigger data-testid={`form-${module}-${f.key}`}><SelectValue placeholder="–" /></SelectTrigger>
          <SelectContent>{opts.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    if (f.type === "multiselect") {
      const opts = f.optionsList ? f.optionsList.map((s) => ({ id: s, label: s })) : plOptions(f.options);
      const arr = Array.isArray(val) ? val : [];
      return (
        <div className="grid grid-cols-2 gap-1.5 border rounded-md p-2 max-h-40 overflow-y-auto" data-testid={`form-${module}-${f.key}`}>
          {opts.map((o) => (
            <label key={o.id} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={arr.includes(o.id)} onCheckedChange={(c) => set(f.key, c ? [...arr, o.id] : arr.filter((x) => x !== o.id))} />
              <span className="truncate">{o.label}</span>
            </label>
          ))}
        </div>
      );
    }
    if (f.type === "account_ref") {
      const nameKey = f.key === "parent_account_id" ? "parent_account_name" : "account_name";
      return (
        <SearchSelect endpoint="/accounts" value={val || ""} initialLabel={form[nameKey] || ""}
          testId={`form-${module}-${f.key}`}
          onChange={(id, item) => { set(f.key, id); set(nameKey, item?.label || ""); }} />
      );
    }
    if (f.type === "opp_ref") {
      return (
        <SearchSelect endpoint="/opportunities" value={val || ""} initialLabel={form.opportunity_name || ""}
          testId={`form-${module}-${f.key}`}
          onChange={(id, item) => { set(f.key, id); set("opportunity_name", item?.label || ""); }} />
      );
    }
    if (f.type === "location_ref") {
      const opts = form.account_id ? locations.filter((l) => l.account_id === form.account_id) : locations;
      return (
        <Select value={val || ""} onValueChange={(v) => set(f.key, v)}>
          <SelectTrigger data-testid={`form-${module}-${f.key}`}><SelectValue placeholder="–" /></SelectTrigger>
          <SelectContent>{opts.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} ({l.country})</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    if (f.type === "plant_ref") {
      const opts = (form.owning_plant_ids || []).length
        ? plants.filter((p) => (form.owning_plant_ids || []).includes(p.id)) : plants;
      return (
        <Select value={val || ""} onValueChange={(v) => set(f.key, v)}>
          <SelectTrigger data-testid={`form-${module}-${f.key}`}><SelectValue placeholder="–" /></SelectTrigger>
          <SelectContent>{opts.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} · {p.name}</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    if (f.type === "plant_multi") {
      const arr = Array.isArray(val) ? val : [];
      return (
        <div className="grid grid-cols-2 gap-1.5 border rounded-md p-2 max-h-40 overflow-y-auto" data-testid={`form-${module}-${f.key}`}>
          {plants.map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={arr.includes(p.id)} data-testid={`form-plant-${p.code}`}
                onCheckedChange={(c) => set(f.key, c ? [...arr, p.id] : arr.filter((x) => x !== p.id))} />
              <span className="truncate"><span className="font-mono text-xs text-[#E8871E]">{p.code}</span> {p.name}</span>
            </label>
          ))}
        </div>
      );
    }
    if (f.type === "user_multi") {
      const arr = Array.isArray(val) ? val : [];
      return (
        <div className="grid grid-cols-2 gap-1.5 border rounded-md p-2 max-h-40 overflow-y-auto" data-testid={`form-${module}-${f.key}`}>
          {users.map((u) => (
            <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={arr.includes(u.id)} data-testid={`form-team-${u.email}`}
                onCheckedChange={(c) => set(f.key, c ? [...arr, u.id] : arr.filter((x) => x !== u.id))} />
              <span className="truncate">{u.name}</span>
            </label>
          ))}
        </div>
      );
    }
    if (f.type === "account_multi") {
      const arr = Array.isArray(val) ? val : [];
      const names = Array.isArray(form.account_names) ? form.account_names : [];
      const initLabels = {};
      arr.forEach((id, i) => { if (names[i]) initLabels[id] = names[i]; });
      return (
        <SearchMultiSelect endpoint="/account-search" values={arr} initialLabels={initLabels}
          testId={`form-${module}-${f.key}`} placeholder={t("accounts")}
          onChange={(ids, labelMap) => { set(f.key, ids); set("account_names", ids.map((id) => labelMap[id]).filter(Boolean)); }} />
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {dupWarning && (
        <div className="bg-amber-50 border border-amber-300 text-amber-800 text-sm rounded-md px-3 py-2" data-testid="duplicate-warning">
          {dupWarning}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[55vh] overflow-y-auto pr-2">
        {fields.map((f) => (
          <div key={f.key} className={["multiselect", "account_multi", "plant_multi", "user_multi", "textarea"].includes(f.type) ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
            <Label className="text-xs font-medium text-gray-600">
              {t(f.label)}{f.key === "first_name" ? " (First)" : f.key === "last_name" ? " (Last)" : ""}
              {f.required && <span className="text-red-500"> *</span>}
            </Label>
            {renderField(f)}
          </div>
        ))}
        <div className="sm:col-span-2 space-y-1.5">
          <Label className="text-xs font-medium text-gray-600">{t("tags")}</Label>
          <Input data-testid={`form-${module}-tags`} placeholder="tag1, tag2"
            value={Array.isArray(form.tags) ? form.tags.join(", ") : ""}
            onChange={(e) => set("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t">
        {allowDelete && initial.id && (
          <Button variant="outline" onClick={() => setDeleteOpen(true)} disabled={saving}
            data-testid="form-delete-btn"
            className="mr-auto border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700">
            <Trash2 className="h-4 w-4 mr-1.5" />{t("delete")}
          </Button>
        )}
        <Button variant="outline" onClick={onCancel} data-testid="form-cancel-btn">{t("cancel")}</Button>
        <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={handleSaveClick} disabled={saving} data-testid="form-save-btn">
          {saving ? "…" : t("save")}
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="form-confirm-save-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_save_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("confirm_save_changes")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="form-confirm-save-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="form-confirm-save-ok"
              className="bg-[#1B2A4A] hover:bg-[#2a3d63]"
              onClick={() => { setConfirmOpen(false); save(); }}>{t("save")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="form-confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete_contact")}</AlertDialogTitle>
            <AlertDialogDescription>{t("confirm_delete_contact")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="form-confirm-delete-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="form-confirm-delete-ok"
              className="bg-red-600 hover:bg-red-700"
              onClick={doDelete}>{t("delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
